import {
  Alert,
  Button,
  Checkbox,
  FormControlLabel,
  InputLabel,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { PasswordChecklist } from 'components/shared/PasswordChecklist';
import { PasswordField } from 'components/shared/PasswordField';
import { Source } from 'dtos/users/common';
import { observer } from 'mobx-react-lite';
import { KeyboardEvent, useEffect } from 'react';
import { EditUserStore } from 'stores/users/EditUserStore';
import { editUser as editUserLabels } from 'utils/labels';

type EditUserProps = {
  scrollToTop: () => void;
  editUserStore: EditUserStore;
};

const EditUser = observer(({ scrollToTop, editUserStore }: EditUserProps) => {
  const { form, message, status, editUser, canEditRoles, editingUser } = editUserStore;
  const { source } = editingUser || {};
  const passwordField = form.$('password');

  useEffect(() => {
    if (message) scrollToTop();
  }, [message, scrollToTop]);

  const handleKeyInField = (evt: KeyboardEvent) => {
    if (evt.key === 'Enter') {
      evt.stopPropagation();
      evt.preventDefault();
      if (form.isValid) {
        editUser();
      }
    }
  };

  return (
    <div aria-label="edit user page" style={{ width: 400, marginTop: 60, marginBottom: 30 }}>
      <form autoComplete="off">
        <Stack spacing={4}>
          <Typography variant="h5">
            <strong>Edit User</strong>
          </Typography>
          {message && (
            <Alert aria-label="form-error" severity={status === 'error' ? 'error' : 'success'}>
              {message}
            </Alert>
          )}
          <InputLabel>
            {editUserLabels.username}: {editingUser?.username}
          </InputLabel>
          {source === Source.LOCAL && (
            <>
              <PasswordField
                variant="outlined"
                autoComplete="new-password"
                onKeyDown={handleKeyInField}
                data-testid="password"
                {...passwordField.bind()}
                inputProps={{
                  role: 'textbox',
                }}
                helperText={undefined}
              />
              <PasswordChecklist style={{ marginTop: '16px' }} password={passwordField.value} />
            </>
          )}
          <TextField
            variant="outlined"
            autoComplete="off"
            onKeyDown={handleKeyInField}
            data-testid="email"
            required
            {...form.$('email').bind()}
          />
          <Stack spacing={2}>
            <div>
              <Tooltip title="User can log in" placement="top-start">
                <FormControlLabel
                  disabled={!canEditRoles}
                  control={<Checkbox data-testid="enabled" {...form.$('enabled').bind()} />}
                  label={editUserLabels.enabled}
                />
              </Tooltip>
            </div>
            <div>
              <Tooltip title="User can edit settings for non-internal users" placement="top-start">
                <FormControlLabel
                  disabled={!canEditRoles}
                  control={<Checkbox data-testid="internal" {...form.$('internal').bind()} />}
                  label={editUserLabels.internal}
                />
              </Tooltip>
            </div>
            <div>
              <Tooltip title="User can edit other users" placement="top-start">
                <FormControlLabel
                  disabled={!canEditRoles}
                  control={<Checkbox data-testid="admin" {...form.$('admin').bind()} />}
                  label={editUserLabels.admin}
                />
              </Tooltip>
            </div>
          </Stack>

          <Button
            variant="contained"
            disableElevation
            style={{ height: 56 }}
            disabled={!form.isValid}
            onClick={() => editUser()}
          >
            Update User
          </Button>
        </Stack>
      </form>
    </div>
  );
});

export { EditUser };
